import i18n from "i18n";
import * as Yup from "yup";

interface EducationUpdateFormFields {
  id?: string | null;
  title: string;
  archived?: boolean;
  isSignpostDefault?: boolean;
  initProgressFromIntakeTestEnabled?: boolean;
}

export interface SchoolUpdateFormFields {
  title: string;
  domain: string;
  educations: Array<EducationUpdateFormFields>;
  sebEnabled?: boolean;
  schoolyearEnabled?: boolean;
  schoolyearKey?: string | null;
  immersiveReaderEnabled?: boolean;
  signpostLoginEnabled?: boolean;
}

export const buildUpdateFormSchema = (): Yup.SchemaOf<SchoolUpdateFormFields> =>
  Yup.object({
    title: Yup.string().required(
      i18n.t("admin-environment:schoolDetails.form.name.errors.required"),
    ),
    domain: Yup.string().required(
      i18n.t("admin-environment:schoolDetails.form.domain.errors.required"),
    ),
    educations: Yup.array()
      .of(
        Yup.object({
          id: Yup.string().nullable(),
          title: Yup.string().required(
            i18n.t("admin-environment:schoolDetails.form.education.name.errors.required"),
          ),
          archived: Yup.boolean().optional(),
          isSignpostDefault: Yup.boolean().optional(),
          initProgressFromIntakeTestEnabled: Yup.boolean().optional(),
        }),
      )
      // Validate that only one education is set as the default when Signpost is enabled
      .when("signpostLoginEnabled", {
        is: true,
        // biome-ignore lint/suspicious/noThenProperty: This "then" keyword is valid "Yup" syntax, cannot be avoided
        then: (schema) =>
          schema.test({
            name: "onlyOneDefault",
            message: i18n.t(
              "admin-environment:schoolDetails.form.education.isSignpostDefaultToggle.errors.onlyOneDefault",
            ),
            test: (value) => {
              const defaultEducationCount =
                value?.reduce(
                  (total, currentEducation) =>
                    currentEducation.isSignpostDefault ? total + 1 : total,
                  0,
                ) || 0;
              return defaultEducationCount === 1;
            },
          }),
      })
      .required(),
    immersiveReaderEnabled: Yup.boolean().optional(),
    signpostLoginEnabled: Yup.boolean().optional(),
    sebEnabled: Yup.boolean().optional(),
    schoolyearEnabled: Yup.boolean().optional(),
    schoolyearKey: Yup.string()
      .nullable()
      .optional()
      // We want to make the key required only when SY toggle is enabled
      .when("schoolyearEnabled", {
        is: true,
        // biome-ignore lint/suspicious/noThenProperty: This "then" keyword is valid "Yup" syntax, cannot be avoided
        then: (schema) =>
          schema.required(
            i18n.t("admin-environment:schoolDetails.form.schoolyearKey.errors.required"),
          ),
      }),
  }).required();
